import * as React from 'react'
import PropTypes from 'prop-types'
import { useEmblaCarousel } from 'embla-carousel/react'
import makeStyles from '@material-ui/core/styles/makeStyles'
import Media from '@oakwood/oui/Media'
import MediaReveal from '@oakwood/oui/MediaReveal'
import { ASPECT_RATIOS } from 'utils/constants'
import Container from 'components/Container'
import Section from 'components/Section'
import SlideshowNavigation from 'components/SlideshowNavigation'
import Typography from 'components/Typography'

const BREAKPOINT_KEY_UP = 'sm'

export const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: -1,
    borderTop: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  header: {
    ...theme.mixins.verticalRhythm(3),
    ...theme.mixins.contain(480),
    marginBottom: 'var(--coa-section-spacing)',
    textAlign: 'center',
  },
  heading: {
    [theme.breakpoints.up(BREAKPOINT_KEY_UP)]: theme.typography.h2,
  },
  text: {},
  main: {
    overflow: 'hidden',
    padding: theme.spacing(0, 4),
  },
  emblaRoot: {
    [theme.breakpoints.up(BREAKPOINT_KEY_UP)]: {
      overflow: 'hidden',
    },
  },
  emblaWrapper: {
    display: 'flex',
    margin: theme.spacing(0, -1),
  },
  emblaSlide: {
    position: 'relative',
    flexShrink: 0,
    width: '100%',
    padding: theme.spacing(0, 1),
    [theme.breakpoints.up('sm')]: {
      width: '50%',
    },
    [theme.breakpoints.up('md')]: {
      width: '25%',
    },
  },
  navigation: {
    marginTop: 'calc(var(--coa-section-spacing) / 2)',
  },
}))

function MediaGroup(props) {
  const { heading, images, text } = props
  const classes = useStyles(props)

  const [emblaRef, embla] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
  })

  const [prevBtnEnabled, setPrevBtnEnabled] = React.useState(false)
  const [nextBtnEnabled, setNextBtnEnabled] = React.useState(false)
  const [selectedIndex, setSelectedIndex] = React.useState(0)
  const [scrollSnaps, setScrollSnaps] = React.useState([])

  const handlePrev = React.useCallback(() => embla && embla.scrollPrev(), [embla])
  const handleNext = React.useCallback(() => embla && embla.scrollNext(), [embla])

  const handleSelect = React.useCallback(() => {
    setSelectedIndex(embla?.selectedScrollSnap())
    setPrevBtnEnabled(embla?.canScrollPrev())
    setNextBtnEnabled(embla?.canScrollNext())
  }, [embla, setSelectedIndex])

  React.useEffect(() => {
    if (!embla) return
    handleSelect()
    setScrollSnaps(embla.scrollSnapList())
    embla.on('select', handleSelect)
  }, [embla, handleSelect])

  return (
    <Section className={classes.root} gutters="padding">
      {(heading || text) && (
        <header className={classes.header}>
          {heading && (
            <Typography className={classes.heading} component="h1" variant="h4">
              {heading}
            </Typography>
          )}

          {text && <Typography>{text}</Typography>}
        </header>
      )}

      <Container className={classes.main}>
        <div className={classes.emblaRoot} ref={emblaRef}>
          <div className={classes.emblaWrapper}>
            {images?.map((image, idx) => (
              <div key={idx} className={classes.emblaSlide}>
                <MediaReveal
                  key={idx}
                  className={classes.mediaContainer}
                  {...ASPECT_RATIOS.article}
                >
                  <Media className={classes.media} src={image} />
                </MediaReveal>
              </div>
            ))}
          </div>
        </div>

        <SlideshowNavigation
          className={classes.navigation}
          listLength={scrollSnaps.length}
          nextButtonDisabled={!nextBtnEnabled}
          onNextClick={handleNext}
          onPrevClick={handlePrev}
          prevButtonDisabled={!prevBtnEnabled}
          selectedIndex={selectedIndex}
        />
      </Container>
    </Section>
  )
}

MediaGroup.propTypes = {
  heading: PropTypes.string,
  images: PropTypes.arrayOf(PropTypes.string).isRequired,
  text: PropTypes.string,
}

export default MediaGroup
